import { sortArcMoments } from "./entityArc";
import { entityEffectiveColor } from "./entityColors";
import { relationLabel } from "./entityLinks";
import { isJust, isNothing, just, nothing, type Maybe } from "./maybe";
import type { ZwGraphNodePos } from "./generated/zwGraph";
import { manuscriptSceneLabel } from "./manuscriptScenes";
import { databaseEntryLabel } from "./storyboardArcs";
import type { DatabaseEntrySummary } from "./tauri/database";
import {
  scenePathsForChapters,
  type ZwChapter,
  type ZwDatabaseRelationPair,
} from "./zwConfig";

export type GraphNodeKind = "entry" | "scene";

export const SCENE_NODE_PREFIX = "scene:";

export const GRAPH_NODE_W_ENTRY = 140;
export const GRAPH_NODE_H_ENTRY = 48;
export const GRAPH_NODE_H_SCENE_SINGLE = 58;
export const GRAPH_NODE_H_SCENE_MULTI = 72;
export const GRAPH_NODE_W_SCENE_MIN = 168;
export const GRAPH_NODE_W_SCENE_MAX = 300;

/** Split `Chapter · Scene` labels onto two lines inside scene nodes. */
export function sceneNodeLabelLines(label: string): string[] {
  const parts = label
    .split(" · ")
    .map((part) => part.trim())
    .filter((value) => value.length > 0);
  if (parts.length >= 2) return parts.slice(0, 2);
  return [label];
}

export function sceneNodeSizeFromLabel(label: string): {
  w: number;
  h: number;
} {
  const lines = sceneNodeLabelLines(label);
  const longest = Math.max(...lines.map((line) => line.length), 1);
  const w = Math.min(
    GRAPH_NODE_W_SCENE_MAX,
    Math.max(GRAPH_NODE_W_SCENE_MIN, Math.ceil(longest * 7.8) + 48),
  );
  const h =
    lines.length > 1 ? GRAPH_NODE_H_SCENE_MULTI : GRAPH_NODE_H_SCENE_SINGLE;
  return { w, h };
}

export function sceneNodeId(scenePath: string): string {
  return `${SCENE_NODE_PREFIX}${scenePath}`;
}

export function isSceneNodeId(id: string): boolean {
  return id.startsWith(SCENE_NODE_PREFIX);
}

export function scenePathFromNodeId(id: string): Maybe<string> {
  if (isSceneNodeId(id) === false) return nothing;
  return just(id.slice(SCENE_NODE_PREFIX.length));
}

export function graphNodeSize(node: GraphNode | GraphNodeKind): {
  w: number;
  h: number;
} {
  if (typeof node === "string") {
    return node === "scene"
      ? sceneNodeSizeFromLabel("Scene")
      : { w: GRAPH_NODE_W_ENTRY, h: GRAPH_NODE_H_ENTRY };
  }
  if (node.kind === "scene") return sceneNodeSizeFromLabel(node.label);
  return { w: GRAPH_NODE_W_ENTRY, h: GRAPH_NODE_H_ENTRY };
}

export type GraphNode = {
  id: string;
  kind: GraphNodeKind;
  label: string;
  type: Maybe<string>;
  color: Maybe<string>;
  tags: string[];
  x: number;
  y: number;
  /** Manuscript path (`Chapters/...`) for scene nodes. */
  scenePath?: string;
};

export type GraphEdgeKind = "link" | "arc";

export type GraphEdge = {
  id: string;
  kind: GraphEdgeKind;
  sourceId: string;
  targetId: string;
  relation: string;
  relationLabel: string;
  note: Maybe<string>;
};

export type GraphLayout = Record<string, ZwGraphNodePos>;

export type GraphFilters = {
  types: Maybe<Set<string>>;
  tags: Maybe<Set<string>>;
};

/** Optional arc-moment overlay — manuscript scene nodes + character→scene edges. */
export type ArcGraphOptions = {
  enabled: boolean;
  /** Entry basenames; when empty, every entry with linked arc moments. */
  characters: Maybe<Set<string>>;
};

export type BuildGraphOptions = {
  arc?: ArcGraphOptions;
  chapters?: ZwChapter[];
  relationPairs?: Maybe<readonly ZwDatabaseRelationPair[]>;
};

const SCENE_ROW_GAP = 200;
const SCENE_NODE_GAP = 200;
const SCENE_CLUSTER_OFFSET = 108;
const SCENE_NODE_RADIUS = 92;
const ENTRY_NODE_RADIUS = 72;

const NODE_GAP = 180;
const DEFAULT_RADIUS = 220;

/** Markdown worldbuilding entries only — maps have no `links[]`. */
export function graphableEntries(
  entries: DatabaseEntrySummary[],
): DatabaseEntrySummary[] {
  return entries.filter((entry) => entry.isMap === false);
}

export function filterGraphEntries(
  entries: DatabaseEntrySummary[],
  filters: GraphFilters,
): DatabaseEntrySummary[] {
  return graphableEntries(entries).filter((entry) => {
    if (isJust(filters.types) && filters.types.value.size > 0) {
      const typeM = entry.fields.type;
      if (isNothing(typeM) || filters.types.value.has(typeM.value) === false) {
        return false;
      }
    }
    if (isJust(filters.tags) && filters.tags.value.size > 0) {
      const tagSet = filters.tags.value;
      if (entry.fields.tags.some((tag) => tagSet.has(tag)) === false) {
        return false;
      }
    }
    return true;
  });
}

/** Circular auto-layout — regenerating is fine (plan lean v1). Positions are
 * centered on the origin; the canvas pans to fit. */
export function autoLayoutPositions(ids: string[]): GraphLayout {
  const layout: GraphLayout = {};
  const n = ids.length;
  if (n === 0) return layout;
  if (n === 1) {
    layout[ids[0]!] = { x: 0, y: 0 };
    return layout;
  }
  const radius = Math.max(DEFAULT_RADIUS, (n * NODE_GAP) / (2 * Math.PI));
  ids.forEach((id, index) => {
    const angle = (2 * Math.PI * index) / n - Math.PI / 2;
    layout[id] = {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  });
  return layout;
}

/** Merge saved layout with auto-layout for any new nodes missing positions. */
export function resolveLayout(ids: string[], saved: GraphLayout): GraphLayout {
  const missing = ids.filter((id) => saved[id] === null);
  const generated = missing.length > 0 ? autoLayoutPositions(missing) : {};
  const layout: GraphLayout = { ...saved };
  for (const id of ids) {
    layout[id] = saved[id] ?? generated[id] ?? { x: 0, y: 0 };
  }
  return layout;
}

/** Entries that have at least one arc moment linked to a manuscript scene. */
export function arcCharacterEntries(
  entries: DatabaseEntrySummary[],
): DatabaseEntrySummary[] {
  return graphableEntries(entries).filter((entry) =>
    entry.fields.arc.some((moment) => isJust(moment.link)),
  );
}

function graphCentroid(nodes: { x: number; y: number }[]): {
  x: number;
  y: number;
} {
  if (nodes.length === 0) return { x: 0, y: 0 };
  return {
    x: nodes.reduce((sum, node) => sum + node.x, 0) / nodes.length,
    y: nodes.reduce((sum, node) => sum + node.y, 0) / nodes.length,
  };
}

function outwardUnit(
  from: { x: number; y: number },
  center: { x: number; y: number },
): { x: number; y: number } {
  const dx = from.x - center.x;
  const dy = from.y - center.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return { x: 0, y: 1 };
  return { x: dx / len, y: dy / len };
}

function nudgeAwayFromNodes(
  pos: { x: number; y: number },
  obstacles: { x: number; y: number; r: number }[],
): { x: number; y: number } {
  let { x, y } = pos;
  for (let pass = 0; pass < 10; pass++) {
    let moved = false;
    for (const obstacle of obstacles) {
      const dx = x - obstacle.x;
      const dy = y - obstacle.y;
      const minDist = obstacle.r + SCENE_NODE_RADIUS;
      const dist = Math.hypot(dx, dy);
      if (dist < minDist) {
        const push = minDist - dist + 2;
        const ux = dist > 0.5 ? dx / dist : 1;
        const uy = dist > 0.5 ? dy / dist : 0;
        x += ux * push;
        y += uy * push;
        moved = true;
      }
    }
    if (moved === false) break;
  }
  return { x, y };
}

function sortScenePaths(scenePaths: string[], chapters: ZwChapter[]): string[] {
  const order = scenePathsForChapters(chapters);
  return [...scenePaths].sort((a, b) => {
    const rankA = order.indexOf(a) === -1 ? 9999 : order.indexOf(a);
    const rankB = order.indexOf(b) === -1 ? 9999 : order.indexOf(b);
    const diff = rankA - rankB;
    if (diff !== 0) return diff;
    return a.localeCompare(b);
  });
}

/** Map manuscript scene paths to the entry ids that link to them via arc moments. */
export function sceneSourcesFromArcEdges(
  edges: Pick<GraphEdge, "kind" | "sourceId" | "targetId">[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const edge of edges) {
    if (edge.kind !== "arc") continue;
    const pathM = scenePathFromNodeId(edge.targetId);
    if (isNothing(pathM)) continue;
    const path = pathM.value;
    const sources = map.get(path) ?? [];
    if (sources.includes(edge.sourceId) === false) sources.push(edge.sourceId);
    map.set(path, sources);
  }
  return map;
}

/** Place scene nodes near the characters that link to them (Obsidian-style clustering). */
export function autoLayoutSceneNodes(
  scenePaths: string[],
  chapters: ZwChapter[],
  entryNodes: Pick<GraphNode, "id" | "x" | "y">[],
  sceneSources: Map<string, string[]>,
): GraphLayout {
  const layout: GraphLayout = {};
  if (scenePaths.length === 0) return layout;

  const entryPos = new Map(
    entryNodes.map((node) => [node.id, { x: node.x, y: node.y }]),
  );
  const center = graphCentroid(entryNodes);
  const obstacles: { x: number; y: number; r: number }[] = entryNodes.map(
    (node) => ({ x: node.x, y: node.y, r: ENTRY_NODE_RADIUS }),
  );

  const groups = new Map<string, string[]>();
  for (const path of scenePaths) {
    const sources = [...(sceneSources.get(path) ?? [])].sort();
    const joined = sources.join("|");
    const key = joined.length > 0 ? joined : path;
    const bucket = groups.get(key) ?? [];
    bucket.push(path);
    groups.set(key, bucket);
  }

  for (const paths of groups.values()) {
    const sorted = sortScenePaths(paths, chapters);
    const firstPath = sorted.at(0);
    if (typeof firstPath === "undefined") continue;
    const sources = [...(sceneSources.get(firstPath) ?? [])].sort();
    const sourcePoints = sources
      .map((id) => entryPos.get(id))
      .filter((point): point is { x: number; y: number } => point !== null);

    if (sourcePoints.length === 0) {
      sorted.forEach((path, index) => {
        const x =
          index * SCENE_NODE_GAP - ((sorted.length - 1) * SCENE_NODE_GAP) / 2;
        const y = center.y + SCENE_ROW_GAP;
        const pos = nudgeAwayFromNodes({ x, y }, obstacles);
        layout[sceneNodeId(path)] = pos;
        obstacles.push({ ...pos, r: SCENE_NODE_RADIUS });
      });
      continue;
    }

    const anchor = graphCentroid(sourcePoints);
    const out = outwardUnit(anchor, center);
    const baseAngle = Math.atan2(out.y, out.x);
    const spread =
      sorted.length > 1
        ? Math.min(Math.PI / 2, (Math.PI / 6) * (sorted.length - 1))
        : 0;

    sorted.forEach((path, index) => {
      const t = sorted.length === 1 ? 0.5 : index / (sorted.length - 1);
      const angle = baseAngle - spread / 2 + spread * t;
      const pos = nudgeAwayFromNodes(
        {
          x: anchor.x + Math.cos(angle) * SCENE_CLUSTER_OFFSET,
          y: anchor.y + Math.sin(angle) * SCENE_CLUSTER_OFFSET,
        },
        obstacles,
      );
      layout[sceneNodeId(path)] = pos;
      obstacles.push({ ...pos, r: SCENE_NODE_RADIUS });
    });
  }

  return layout;
}

function appendArcOverlay(
  entries: DatabaseEntrySummary[],
  entryNodes: GraphNode[],
  layout: GraphLayout,
  arc: ArcGraphOptions,
  chapters: ZwChapter[],
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const visibleEntryIds = new Set(entryNodes.map((node) => node.id));
  const arcSources = arcCharacterEntries(entries).filter((entry) => {
    if (
      isJust(arc.characters) &&
      arc.characters.value.size > 0 &&
      arc.characters.value.has(entry.name) === false
    ) {
      return false;
    }
    return visibleEntryIds.has(entry.name);
  });

  const scenePaths = new Set<string>();
  const sceneToSources = new Map<string, string[]>();
  const arcEdges: GraphEdge[] = [];

  for (const entry of arcSources) {
    for (const arcMoment of sortArcMoments(entry.fields.arc)) {
      if (isNothing(arcMoment.link)) continue;
      const path = arcMoment.link.value.file.trim();
      if (path.length === 0) continue;
      scenePaths.add(path);
      const sources = sceneToSources.get(path) ?? [];
      if (sources.includes(entry.name) === false) sources.push(entry.name);
      sceneToSources.set(path, sources);
      arcEdges.push({
        id: `${entry.name}:${arcMoment.id}`,
        kind: "arc",
        sourceId: entry.name,
        targetId: sceneNodeId(path),
        relation: "arc_moment",
        relationLabel: arcMoment.moment,
        note: nothing,
      });
    }
  }

  const sceneIds = [...scenePaths].map(sceneNodeId);
  const sceneLayoutSeed = autoLayoutSceneNodes(
    [...scenePaths],
    chapters,
    entryNodes,
    sceneToSources,
  );
  const resolvedScenes = resolveLayout(sceneIds, {
    ...sceneLayoutSeed,
    ...layout,
  });

  const sceneNodes: GraphNode[] = [...scenePaths].map((path) => {
    const id = sceneNodeId(path);
    const pos = resolvedScenes[id] ?? { x: 0, y: 0 };
    return {
      id,
      kind: "scene",
      label: manuscriptSceneLabel(path, chapters),
      type: nothing,
      color: nothing,
      tags: [],
      x: pos.x,
      y: pos.y,
      scenePath: path,
    };
  });

  return { nodes: sceneNodes, edges: arcEdges };
}

export function buildGraph(
  entries: DatabaseEntrySummary[],
  layout: GraphLayout,
  filters: GraphFilters = { types: nothing, tags: nothing },
  options: BuildGraphOptions = {},
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  let visible = filterGraphEntries(entries, filters);
  const arc = options.arc;
  if (
    typeof arc !== "undefined" &&
    arc.enabled === true &&
    isJust(arc.characters) &&
    arc.characters.value.size > 0
  ) {
    const visibleIds = new Set(visible.map((entry) => entry.name));
    for (const entry of graphableEntries(entries)) {
      if (
        arc.characters.value.has(entry.name) === false ||
        visibleIds.has(entry.name)
      ) {
        continue;
      }
      visible = [...visible, entry];
      visibleIds.add(entry.name);
    }
  }

  const visibleIds = new Set(visible.map((entry) => entry.name));
  const resolved = resolveLayout([...visibleIds], layout);

  const nodes: GraphNode[] = visible.map((entry) => {
    const pos = resolved[entry.name] ?? { x: 0, y: 0 };
    return {
      id: entry.name,
      kind: "entry",
      label: databaseEntryLabel(entry),
      type: entry.fields.type,
      color: entityEffectiveColor(entry.fields.color, entry.fields.type),
      tags: entry.fields.tags,
      x: pos.x,
      y: pos.y,
    };
  });

  const edges: GraphEdge[] = [];
  for (const entry of visible) {
    for (const link of entry.fields.links) {
      if (visibleIds.has(link.file) === false) continue;
      edges.push({
        id: `${entry.name}:${link.id}`,
        kind: "link",
        sourceId: entry.name,
        targetId: link.file,
        relation: link.relation,
        relationLabel: relationLabel(link.relation, options.relationPairs),
        note: link.note,
      });
    }
  }

  const arcOptions = options.arc;
  if (typeof arcOptions === "undefined" || arcOptions.enabled !== true) {
    return { nodes, edges };
  }

  const overlay = appendArcOverlay(
    entries,
    nodes,
    layout,
    arcOptions,
    options.chapters ?? [],
  );

  return {
    nodes: [...nodes, ...overlay.nodes],
    edges: [...edges, ...overlay.edges],
  };
}

/** Normalizes graph layout read from `Database/.zwgraph` or IPC. */
export function normalizeGraphLayout(raw: unknown): GraphLayout {
  if (raw === null || typeof raw !== "object") return {};
  const layout: GraphLayout = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (
      value !== null &&
      typeof value === "object" &&
      typeof (value as { x?: unknown }).x === "number" &&
      typeof (value as { y?: unknown }).y === "number"
    ) {
      layout[key] = {
        x: (value as { x: number }).x,
        y: (value as { y: number }).y,
      };
    }
  }
  return layout;
}

/** Loads graph layout from `Database/.zwgraph`. */
export async function loadGraphLayout(rootPath: string): Promise<GraphLayout> {
  const { getProjectGraphLayout } = await import("./tauri/database");
  try {
    return normalizeGraphLayout(await getProjectGraphLayout(rootPath));
  } catch (error) {
    console.error("Failed to load graph layout from Database/.zwgraph", error);
    return {};
  }
}

/** Persists graph layout to `Database/.zwgraph` (committed with the project). */
export async function persistGraphLayout(
  rootPath: string,
  layout: GraphLayout,
): Promise<void> {
  const { setProjectGraphLayout } = await import("./tauri/database");
  try {
    await setProjectGraphLayout(rootPath, layout);
  } catch (error) {
    console.error("Failed to save graph layout to Database/.zwgraph", error);
  }
}
